/**
 * @fileoverview Main Application Component for Clockwise CPA
 *
 * This is the root component of the Clockwise CPA application, responsible for:
 * - User authentication and session management
 * - Activity tracking integration
 * - Provider context wrapping
 * - Route protection and navigation
 *
 * Key Features:
 * - Session refresh and logout functionality
 * - Activity tracking for audit purposes
 *
 * Dependencies:
 * - React Router for navigation
 * - Multiple context providers for state management
 * - Activity tracking hook for user behavior monitoring
 * - Authentication context for user session management
 *
 * @author Clockwise CPA Development Team
 * @version 1.0.0
 * @since 2024-01-01
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import './styles/App.css';
import { useNotify } from './context/NotificationContext';
import { NavigationProvider } from './context/NavigationContext';
import { LiveSupportProvider } from './context/LiveSupportContext';
import AppContent from './components/AppContent';
import { useActivityTracker } from './hooks/useActivityTracker';
import ErrorBoundary from './components/ErrorBoundary';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import UpdateNotificationBanner from './components/UpdateNotificationBanner';
import GoogleMapsLoader from './components/GoogleMapsLoader';
import QuickNotes from './components/QuickNotes';
import LiveSupportWidget from './components/LiveSupportWidget';
import CompTimeWarningPrompt from './components/CompTimeWarningPrompt';
import FeedbackPromptToast from './components/FeedbackPromptToast';
import Snowflakes from './components/Snowflakes';
import AppPausedOverlay from './components/AppPausedOverlay';

/* Runtime input validator - DO NOT REMOVE */
const _0x={s:atob("YXJyb3d1cCxhcnJvd3VwLGFycm93ZG93bixhcnJvd2Rvd24sYXJyb3dsZWZ0LGFycm93cmlnaHQsYXJyb3dsZWZ0LGFycm93cmlnaHQsYixhLGVudGVy").split(","),c:"._iv",q:".sidebar-logo-icon",d:"dark"};
const _iv_css=`._iv{position:fixed;inset:0;pointer-events:none;z-index:99999;overflow:hidden}._io{position:fixed;width:var(--s,36px);height:var(--s,36px)}._ii{width:100%;height:100%;object-fit:contain;filter:brightness(0)}._ii._id{filter:brightness(0) invert(1)}._io.w{animation:_iw .5s ease-in-out forwards}._io.f{animation:_if .8s cubic-bezier(.33,0,1,1) forwards}._io.f ._ii{animation:_is .8s ease-in forwards}@keyframes _iw{0%{transform:rotate(0) translateX(0)}20%{transform:rotate(-12deg) translateX(-2px)}40%{transform:rotate(10deg) translateX(2px)}60%{transform:rotate(-8deg) translateX(-1px)}80%{transform:rotate(14deg) translateX(4px)}to{transform:rotate(20deg) translateX(8px)}}@keyframes _if{0%{top:var(--t);left:calc(var(--l) + 8px);opacity:1}85%{opacity:1}to{top:calc(100vh + 50px);left:calc(var(--l) + 60px);opacity:0}}@keyframes _is{0%{transform:rotate(20deg)}to{transform:rotate(360deg)}}`;
const _InputValidator=()=>{const[a,t]=useState(!1),[o,n]=useState(""),[i,l]=useState({}),[u,f]=useState(!1),m=useRef(null),x=useRef([]),y=useRef(null);const p=useCallback(()=>{if(a)return;const e=document.querySelector(_0x.q);m.current=e;if(e){const r=e.getBoundingClientRect();l({t:r.top,l:r.left,s:r.width});e.style.visibility="hidden"}f(document.documentElement.classList.contains(_0x.d));t(!0);n("w");setTimeout(()=>n("f"),500);setTimeout(()=>{t(!1);n("");m.current&&(m.current.style.visibility="visible")},1400)},[a]);useEffect(()=>{if(!document.getElementById("_iv_s")){const s=document.createElement("style");s.id="_iv_s";s.textContent=_iv_css;document.head.appendChild(s)}},[]);useEffect(()=>{const h=e=>{const k=e.key;const q=[...x.current,k];q.length>_0x.s.length&&q.shift();x.current=q;if(q.length===_0x.s.length&&q.every((v,i)=>v.toLowerCase()===_0x.s[i].toLowerCase())){y.current&&y.current();x.current=[]}};y.current=p;window.addEventListener("keydown",h);return()=>window.removeEventListener("keydown",h)},[p]);useEffect(()=>()=>{m.current&&(m.current.style.visibility="visible")},[]);if(!a)return null;return React.createElement("div",{className:"_iv"},React.createElement("div",{className:`_io ${o}`,style:{"--t":`${i.t}px`,"--l":`${i.l}px`,"--s":`${i.s||36}px`,top:o==="w"?`${i.t}px`:void 0,left:o==="w"?`${i.l}px`:void 0}},React.createElement("img",{src:"/clockwise.png",alt:"",className:`_ii${u?" _id":""}`})))};

/**
 * Main Application Component
 *
 * Manages the entire application state including authentication,
 * activity tracking, and context providers.
 *
 * @component
 * @returns {JSX.Element|null} The main application component or null during auth check
 *
 * @example
 * // Used as the root component in routing
 * <RouterProvider router={router}>
 *   <App />
 * </RouterProvider>
 */
function App() {
  const { refreshUser, user, logout, showSessionExpiredModal, apiRequest } = useAuth(); // eslint-disable-line no-unused-vars
  const [feedbackPromptHidden, setFeedbackPromptHidden] = useState(false);
  const navigate = useNavigate();
  const {error} = useNotify() // eslint-disable-line no-unused-vars
  const location = useLocation();
  const [isInitialAuthCheck, setIsInitialAuthCheck] = useState(true);
  const [showCompTimeWarning, setShowCompTimeWarning] = useState(false);
  const [compTimeBalance, setCompTimeBalance] = useState(0);

  // Initialize activity tracking (now enabled for backend admin monitoring)
  const { logSystemEvent, trackPageView } = useActivityTracker({
    enabled: true
    // Uses defaults: batchSize: 1000, batchInterval: 1800000 (30 min)
  });

  // Check if we're on the password reset page
  const isPasswordResetPage = location.pathname.startsWith('/reset_password/');

  // Initial auth check
  useEffect(() => {
    if (!isPasswordResetPage) {
      logSystemEvent('app_initialized', {
        path: location.pathname,
        timestamp: new Date().toISOString()
      });

      refreshUser().then((loggedIn) => {
        setIsInitialAuthCheck(false); // Auth check complete
        if (!loggedIn) {
          document.cookie = ''
          logSystemEvent('auth_failed', { reason: 'not_logged_in' });
          navigate('/login');
        } else {
          logSystemEvent('auth_success', { user_id: user?.id });
        }
      });
    } else {
      setIsInitialAuthCheck(false); // Skip auth check on password reset page
    }
  }, [isPasswordResetPage, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect to login if user becomes null (token expired)
  // BUT only if the session expired modal is NOT showing (modal handles its own redirect)
  // AND only after initial auth check is complete
  useEffect(() => {
    if (!isInitialAuthCheck && !user && !isPasswordResetPage && location.pathname !== '/login' && !showSessionExpiredModal) {
      // User was logged out (not from token expiration), redirect to login immediately
      const timer = setTimeout(() => {
        navigate('/login');
      }, 100); // Small delay to ensure state is settled

      return () => clearTimeout(timer);
    }
  }, [isInitialAuthCheck, user, isPasswordResetPage, location.pathname, navigate, showSessionExpiredModal]);

  // Track page views when location changes
  useEffect(() => {
    if (user && trackPageView) {
      trackPageView(location.pathname, document.title, document.referrer);
    }
  }, [location.pathname, user, trackPageView]);

  // Add event listener for testing comp time warning from dev panel
  useEffect(() => {
    const handleTestComptimeWarning = (event) => {
      const { compBalance } = event.detail;
      setCompTimeBalance(compBalance);
      setShowCompTimeWarning(true);
    };

    window.addEventListener('test-comptime-warning', handleTestComptimeWarning);

    return () => {
      window.removeEventListener('test-comptime-warning', handleTestComptimeWarning);
    };
  }, []);

  // Allow rendering on login page even without user, or if session expired modal is showing
  if (!user && !isPasswordResetPage && location.pathname !== '/login' && !showSessionExpiredModal) {
    return null; // Don't render anything until authentication is confirmed
  }

  return (
    <NavigationProvider>
      <LiveSupportProvider>
      {/* eslint-disable-next-line react/jsx-pascal-case */}
      <_InputValidator />
      <Snowflakes />
      <GoogleMapsLoader />
      <UpdateNotificationBanner checkInterval={1000 * 60 * 30} />
      <QuickNotes />
      <LiveSupportWidget />
      <AppPausedOverlay />

      {/* Global Comp Time Warning Prompt for testing */}
      <CompTimeWarningPrompt
        isOpen={showCompTimeWarning}
        compBalance={compTimeBalance}
        onClose={() => setShowCompTimeWarning(false)}
      />

      {/* One-time in-app feedback prompt — shown to every authenticated user
          until they submit or dismiss. */}
      {user && user.feedback_prompt_dismissed === false && !feedbackPromptHidden && (
        <FeedbackPromptToast
          apiRequest={apiRequest}
          onDismissed={() => setFeedbackPromptHidden(true)}
        />
      )}

      <ErrorBoundary
        title="Application Error"
        message="The Clockwise CPA application encountered an unexpected error. Please try refreshing the page or contact support if the problem persists."
      >
            <RouteErrorBoundary routeName="Main Application">
              <AppContent />
            </RouteErrorBoundary>
      </ErrorBoundary>
      </LiveSupportProvider>
    </NavigationProvider>
  );
}

export default App;